import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, 
  Bell, 
  Shield, 
  Smartphone,
  Mail,
  Globe,
  Moon,
  Volume2,
  Loader2,
  Camera,
  Building2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { doc, updateDoc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

import { toast } from 'sonner';
import { OrganizationSettings } from '@/components/customer/OrganizationSettings';
import { format } from 'date-fns';
import { PhoneSettingsWithOTP } from '@/components/settings/PhoneSettingsWithOTP';
import { DemographicsInputs } from '@/components/shared/DemographicsInputs';
import { getPatientDemographics, patchDemographics, emptyDemographics, type DemographicsDraft } from '@/lib/firebase/queries/patientDemographics';
import {
  NOTIFICATION_CATEGORY_IDS,
  isCategoryEnabled,
  categoryPatch,
  type NotificationCategoryId,
  type NotificationPreferences,
} from '@/lib/notifications/preferences';

export function SettingsPage() {
  const { userData, setUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t, dir, language } = useLanguage();

  // Form state
  const [firstNameEn, setFirstNameEn] = useState(userData?.firstNameEn || '');
  const [lastNameEn, setLastNameEn] = useState(userData?.lastNameEn || '');
  const [firstNameAr, setFirstNameAr] = useState(userData?.firstNameAr || '');
  const [lastNameAr, setLastNameAr] = useState(userData?.lastNameAr || '');
  const [email, setEmail] = useState(userData?.email || '');
  const [demographics, setDemographics] = useState<DemographicsDraft>(emptyDemographics);

  useEffect(() => {
    if (!userData?.id) return;
    getPatientDemographics(userData.id)
      .then((d) => setDemographics({
        gender: d.gender ?? '',
        dateOfBirth: d.dateOfBirth ? format(d.dateOfBirth, 'yyyy-MM-dd') : '',
      }))
      .catch(() => {});
  }, [userData?.id]);

  // The eight per-category push toggles. These configure what the user's
  // PHONE receives (web has no push channel of its own), read from and
  // written to the same `users/{uid}.notificationPreferences` map the
  // server gate and the mobile app use. Not a security boundary — see
  // src/lib/notifications/preferences.ts.
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>({});

  useEffect(() => {
    if (!userData?.id) return;
    let cancelled = false;
    getDoc(doc(db, 'users', userData.id))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        setNotificationPrefs((snap.data() as any)?.notificationPreferences || {});
      })
      // Fails OPEN: an empty map renders every switch on, which is what an
      // untouched account renders. Rendering off switches after a transient
      // read failure would tell the user they had muted things they had not.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [userData?.id]);

  const handleNotificationToggle = async (category: NotificationCategoryId, enabled: boolean) => {
    if (!userData?.id) return;
    const previous = notificationPrefs;
    setNotificationPrefs({ ...previous, [category]: enabled });
    try {
      await setDoc(doc(db, 'users', userData.id), categoryPatch(category, enabled), { merge: true });
    } catch (error) {
      console.error('Failed to save notification preference', error);
      setNotificationPrefs(previous);
      toast.error(t('settings.notificationSaveError'));
    }
  };

  // Get the appropriate name based on language
  const displayFirstName = language === 'ar' ? userData?.firstNameAr : userData?.firstNameEn;
  const displayLastName = language === 'ar' ? userData?.lastNameAr : userData?.lastNameEn;

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userData) return;

    try {
      setUploadingImage(true);

      // Create a FileReader to read the image
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64Image = reader.result as string;
        
        // Update user data in Firestore
        await updateDoc(doc(db, 'users', userData.id), {
          photoURL: base64Image,
          updatedAt: new Date()
        });

        // Update local state
        setUserData({
          ...userData,
          photoURL: base64Image
        });

        toast.success(t('settings.photoUpdated'));
      };

      reader.onerror = () => {
        toast.error(t('settings.photoUpdateError'));
      };
    } catch (error) {
      console.error('Error updating photo:', error);
      toast.error(t('settings.photoUpdateError'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!userData) return;

    try {
      setLoading(true);

      // Update user document (phone is handled separately by PhoneSettingsWithOTP)
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        firstNameEn,
        lastNameEn,
        firstNameAr,
        lastNameAr,
        email,
        updatedAt: new Date()
      });
      await patchDemographics(userData.id, demographics);
      
      toast.success(t('settings.profileUpdated'));

      // Update local state (phone is updated separately by PhoneSettingsWithOTP)
      setUserData({
        ...userData,
        firstNameEn,
        lastNameEn,
        firstNameAr,
        lastNameAr,
        email
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('settings.profileUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" dir={dir}>
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">{t('settings.personalSettings')}</h1>
        <p className="text-muted-foreground">
          {t('settings.personalSettingsDesc')}
        </p>
      </div>

      <Card className="p-0">
        <Tabs defaultValue="profile" dir={dir}>
          <div className="border-b">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger
                value="profile"
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-primary"
              >
                {t('settings.profile')}
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-primary"
              >
                {t('settings.notifications')}
              </TabsTrigger>
              <TabsTrigger
                value="privacy"
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-primary"
              >
                {t('settings.privacy')}
              </TabsTrigger>
              <TabsTrigger
                value="preferences"
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-primary"
              >
                {t('settings.preferences')}
              </TabsTrigger>
              <TabsTrigger
                value="organization"
                className="rounded-none border-b-2 border-transparent px-4 py-3 data-[state=active]:border-primary"
              >
                {t('settings.organization')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="profile" className="p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-6">
                <div className="relative group">
                  <Avatar className="h-20 w-20 bg-[#7dda55] text-white border-none cursor-pointer" onClick={handleImageClick}>
                    <AvatarImage src={userData?.photoURL} alt={displayFirstName} className="object-cover" />
                    <AvatarFallback className="bg-[#7dda55]">
                      <User className="h-10 w-10" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={handleImageClick}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                      {t('settings.uploading')}
                    </>
                  ) : (
                    t('settings.changePhoto')
                  )}
                </Button>
              </div>

              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstNameEn" className={dir === 'rtl' ? 'text-right' : ''}>
                      {t('settings.firstName')} (بالإنجليزية)
                    </Label>
                    <Input 
                      id="firstNameEn" 
                      value={firstNameEn}
                      onChange={(e) => setFirstNameEn(e.target.value)}
                      placeholder={t('settings.enterFirstName')}
                      dir="ltr"
                      className="text-left"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastNameEn" className={dir === 'rtl' ? 'text-right' : ''}>
                      {t('settings.lastName')} (بالإنجليزية)
                    </Label>
                    <Input 
                      id="lastNameEn" 
                      value={lastNameEn}
                      onChange={(e) => setLastNameEn(e.target.value)}
                      placeholder={t('settings.enterLastName')}
                      dir="ltr"
                      className="text-left"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="firstNameAr" className={dir === 'rtl' ? 'text-right' : ''}>
                      {t('settings.firstName')} (بالعربية)
                    </Label>
                    <Input 
                      id="firstNameAr" 
                      value={firstNameAr}
                      onChange={(e) => setFirstNameAr(e.target.value)}
                      placeholder={t('settings.enterFirstName')}
                      dir="rtl"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastNameAr" className={dir === 'rtl' ? 'text-right' : ''}>
                      {t('settings.lastName')} (بالعربية)
                    </Label>
                    <Input 
                      id="lastNameAr" 
                      value={lastNameAr}
                      onChange={(e) => setLastNameAr(e.target.value)}
                      placeholder={t('settings.enterLastName')}
                      dir="rtl"
                      className="text-right"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className={dir === 'rtl' ? 'text-right' : ''}>
                      {t('settings.email')}
                    </Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('settings.enterEmail')}
                      dir={dir}
                      className={dir === 'rtl' ? 'text-right' : ''}
                    />
                  </div>
                  <DemographicsInputs value={demographics} onChange={setDemographics} disabled={loading} />
                  <div className="space-y-2">
                    <PhoneSettingsWithOTP />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="p-6">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">{t('settings.pushNotifications')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('settings.pushNotificationsPhoneNotice')}
                </p>
                <div className="space-y-4">
                  {NOTIFICATION_CATEGORY_IDS.map((id) => (
                    <div key={id} className="flex items-center justify-between">
                      <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                        <Label>{t(`settings.notificationCategories.${id}`)}</Label>
                        <p className="text-sm text-muted-foreground">
                          {t(`settings.notificationCategoryDescriptions.${id}`)}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <Switch
                          checked={isCategoryEnabled(notificationPrefs, id)}
                          onCheckedChange={(next) => handleNotificationToggle(id, next)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">{t('settings.emailNotifications')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.weeklySummary')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.weeklySummaryDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch defaultChecked />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.newsletter')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.newsletterDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="privacy" className="p-6">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">{t('settings.privacySettings')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.profileVisibility')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.profileVisibilityDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch defaultChecked />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.activityStatus')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.activityStatusDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">{t('settings.dataSettings')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.dataCollection')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.dataCollectionDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preferences" className="p-6">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium">{t('settings.appPreferences')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.darkMode')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.darkModeDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className={`space-y-0.5 ${dir === 'rtl' ? 'text-right' : ''}`}>
                      <Label>{t('settings.soundEffects')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.soundEffectsDesc')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="organization" className="p-6">
            <OrganizationSettings />
          </TabsContent>
        </Tabs>
      </Card>

      <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'} gap-4`}>
        <Button variant="outline">{t('settings.cancel')}</Button>
        <Button onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
              {t('settings.saving')}
            </>
          ) : (
            t('settings.saveChanges')
          )}
        </Button>
      </div>
    </div>
  );
}